import * as _ from 'lodash';
import toastr from 'toastr';
import apiService from '../../../../service/apiService';
import logUtils from '../../../../HelperMethods/LogUtils';
import { sleep } from '../../../../HelperMethods/UtilityFunctions';
import { 
  loadModels, 
  setLoader, 
  setFormLoader, 
  upsertModel, 
  deleteModel as deleteModelFromRedux,
  setFactorsList
} from './reducer';
// Import factors Redux actions to sync activeModel state
import { 
  setActiveModel as setFactorsActiveModel,
  setAllModels as setFactorsAllModels 
} from '../../factors/redux/reducer';

export function loadModelsList(tenantId, fieldId, skipLoader = false) {
  return async (dispatch) => {
    if (!skipLoader) dispatch(setLoader(true));
    let models = [];
    try {
      const response = await apiService.getFactors('score', tenantId, fieldId);
      const modelsData = response?.data?.getFactorsByType || [];
      dispatch(loadModels(modelsData));
      models = modelsData;
      
      // Update factors store with models and active model
      const activeModel = modelsData.find(model => model.isActive) || null;
      dispatch(setFactorsActiveModel(activeModel));
      dispatch(setFactorsAllModels(modelsData));
    } catch (e) {
      dispatch(loadModels([]));
      dispatch(setFactorsActiveModel(null));
      dispatch(setFactorsAllModels([]));
      logUtils.logError(e, 'Loading Models Failed');
    } finally {
      if (!skipLoader) dispatch(setLoader(false));
    }
    return models;
  };
}

export function createModel(model) {
  return async (dispatch, getState) => {
    dispatch(setFormLoader(true));
    try {
      delete model.id; // Removes any undefined/null id
      const response = await apiService.createFactor(model);
      const createdModel = response?.data?.createFactor;
      if (!createdModel) throw new Error('Model not created');
      
      toastr.success(`${model.name} created successfully!`);
      dispatch(upsertModel(createdModel));
      
      // Update factors store when model is created
      const currentState = getState();
      const allModels = currentState.prioritizationModelReducer?.models || [];
      const activeModel = allModels.find(m => m.isActive) || null;
      dispatch(setFactorsActiveModel(activeModel));
      dispatch(setFactorsAllModels(allModels));
      
      return createdModel;
    } catch (e) {
      logUtils.logError(e, 'Model creation failed');
      toastr.error('Failed to create model');
      throw e;
    } finally {
      dispatch(setFormLoader(false));
    }
  };
}

export function updateModel(model, newActiveState = null) {
  return async (dispatch, getState) => {
    dispatch(setFormLoader(true));
    try {
      const response = await apiService.updateFactor({ ...model, type: 'score' });
      const updatedModel = response?.data?.updateFactor;
      if (!updatedModel?.id) {
        throw new Error('Failed to receive updated model from API');
      }

      if (newActiveState !== null) {
        const statusMessage = newActiveState ? 'activated' : 'deactivated';
        toastr.success(`${model.name} has been ${statusMessage}.`);
        dispatch(upsertModel(updatedModel));
      } else {
        toastr.success(`${model.name} updated successfully!`);


        if (model.tenantId && model.fieldId) {
          await dispatch(loadModelsList(model.tenantId, model.fieldId, true));
        } else {
          dispatch(upsertModel(updatedModel));
          
          // Update factors store when model is updated
          const currentState = getState();
          const allModels = currentState.prioritizationModelReducer?.models || [];
          const activeModel = allModels.find(m => m.isActive) || null;
          dispatch(setFactorsActiveModel(activeModel));
          dispatch(setFactorsAllModels(allModels));
        }
      }
      
      return updatedModel;
    } catch (e) {
      logUtils.logError(e, 'Updating Model failed');
      toastr.error(`Failed to update ${model.name}`);
      throw e;
    } finally {
      dispatch(setFormLoader(false));
    }
  };
}

export function deleteModel(model) {
  return async (dispatch, getState) => {
    dispatch(setLoader(true));
    try {
      const response = await apiService.deleteFactor(model.id);
      if (response?.data?.deleteFactor?.id) {
        dispatch(deleteModelFromRedux(model));
        toastr.success(`${model.name} deleted.`);
        
        // Update factors store after deletion
        const currentState = getState();
        const allModels = currentState.prioritizationModelReducer?.models || [];
        const activeModel = allModels.find(m => m.isActive) || null;
        dispatch(setFactorsActiveModel(activeModel));
        dispatch(setFactorsAllModels(allModels));
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (e) {
      logUtils.logError(e, 'Model deletion failed');
      toastr.error('Delete failed.');
    } finally {
      dispatch(setLoader(false));
    }
  };
}

export function getModelById(modelId) {
  return async (dispatch) => {
    dispatch(setFormLoader(true));
    try {
      const response = await apiService.getFactorById(modelId);
      const modelData = response?.data?.getFactorById;
      if (!modelData) throw new Error('Model not found');
      
      return modelData;
    } catch (e) {
      logUtils.logError(e, 'Loading Model by ID failed');
      throw e;
    } finally {
      dispatch(setFormLoader(false));
    }
  };
}

// Load factors for model creation/editing
export function loadFactors(tenantId, fieldId) {
  return async (dispatch) => {
    try {
      const response = await apiService.getFactors('factor', tenantId, fieldId);
      const factors = response?.data?.getFactorsByType || [];
      
      dispatch(setFactorsList(factors));
      return factors;
    } catch (e) {
      logUtils.logError(e, 'Loading Factors for Models failed');
      dispatch(setFactorsList([]));
    }
  };
}

export function onSubmitButtonClicked(model) {
  return async (dispatch) => {
    const clonedModel = _.clone(model);
    if (model.id) {
      return await dispatch(updateModel(clonedModel));
    } else {
      return await dispatch(createModel(clonedModel));
    }
  };
}

//active-inactive toggle for models
export function toggleModelActiveState(model, newActiveState) {
  return async (dispatch, getState) => {
    const updatedModel = { ...model, isActive: newActiveState };
    const result = await dispatch(updateModel(updatedModel, newActiveState));
    
    // Update factors store to sync the activeModel state
    const currentState = getState();
    const allModels = currentState.prioritizationModelReducer?.models || [];
    
    // Find the currently active model after the update
    // If we're activating this model, use it; otherwise find any active model
    const activeModel = newActiveState ? 
                       allModels.find(m => m.id === model.id) || { ...model, isActive: true } :
                       allModels.find(m => m.isActive && m.id !== model.id) || null;
    
    // Update factors store with current active model and all models
    dispatch(setFactorsActiveModel(activeModel));
    dispatch(setFactorsAllModels(allModels));
    
    return result;
  };
}

// Duplicate model
export function duplicateModel(model, tenantId, fieldId) {
  return async (dispatch) => {
    dispatch(setFormLoader(true));
    try {
      const payload = {
        name: `Copy of ${model.name}`,
        description: model.description ?? '',
        displayName: '',
        isActive: false,
        isDeleted: false,
        hasMatrix: false,
        type: 'score',
        tenantId,
        fieldId,
        excludedTaskTypes: [],
        scoreMatrix: {
          formula: model.scoreMatrix?.formula || '',
          factors: model.scoreMatrix?.factors || [],
          priorityMatrix: model.scoreMatrix?.priorityMatrix || [],
        },
      };

      const response = await apiService.createFactor(payload);
      const newModel = response?.data?.createFactor;
      if (!newModel) throw new Error('Model duplication failed');
      
      dispatch(upsertModel(newModel));
      toastr.success('Model duplicated successfully!');
      return newModel;
    } catch (e) {
      logUtils.logError(e, 'Model duplication failed');
      toastr.error('Failed to duplicate model');
      throw e;
    } finally {
      dispatch(setFormLoader(false));
    }
  };
}

// loading factors & models together
export function loadModelsAndFactors(tenantId, fieldId) {
  return async (dispatch) => {
    dispatch(setLoader(true));
    try {
      await Promise.all([
        dispatch(loadModelsList(tenantId, fieldId, true)), // Skip individual loader
        dispatch(loadFactors(tenantId, fieldId))
      ]);
    } catch (e) {
      console.error('Failed to load models and factors', e);
      logUtils.logError(e, 'Loading Models and Factors failed');
    } finally {
      dispatch(setLoader(false));
    }
  };
}

// Complex activation with deactivation of currently active model
export function activateModelWithDeactivation(modelToActivate, currentlyActiveModel) {
  return async (dispatch, getState) => {
    dispatch(setLoader(true));
    try {
      // First deactivate the currently active model if it exists
      if (currentlyActiveModel && currentlyActiveModel.id !== modelToActivate.id) {
        await dispatch(toggleModelActiveState(currentlyActiveModel, false));
        // Wait 1 second for database to process deactivation
        await sleep(1000);
      }
      // Then activate the target model
      await dispatch(toggleModelActiveState(modelToActivate, true));
      
      // Update factors store to ensure activeModel is set correctly
      const currentState = getState();
      const allModels = currentState.prioritizationModelReducer?.models || [];
      const activeModel = allModels.find(m => m.id === modelToActivate.id) || null;
      
      dispatch(setFactorsActiveModel(activeModel));
      dispatch(setFactorsAllModels(allModels));
    } catch (e) {
      logUtils.logError(e, 'Model activation failed');
      toastr.error('Activation failed.');
    } finally {
      dispatch(setLoader(false));
    }
  };
}

//  Score evaluation for playground
export function evaluateModelScore(formula, assetId, taskTypeId, fieldId) {
  return async (dispatch) => {
    try {
      const response = await apiService.getScoreValue({
        formula,
        assetId,
        taskTypeId,
        fieldId,
      });
      
      return {
        score: response?.score ?? null,
        factorValuesMap: response?.factorValues ?? {},
      };
    } catch (e) {
      logUtils.logError(e, 'Score evaluation failed');
      return {
        score: null,
        factorValuesMap: {},
      };
    }
  };
}
